import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type KeyboardEvent,
  type TextareaHTMLAttributes,
} from 'react';
import { Tickets, type TicketSummary } from '../lib/api';

interface Props extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'onChange'> {
  value: string;
  onChange: (next: string) => void;
}

/**
 * Textarea with an inline "#"-hotkey ticket picker. When the user types "#",
 * a popover opens below the textarea showing matching tickets (live, debounced
 * search). Arrow keys navigate; Enter inserts "#&lt;id&gt;" at the cursor and
 * closes the popover. Escape or whitespace dismisses the popover without
 * inserting.
 *
 * The query is whatever the user types AFTER the "#" before whitespace —
 * a numeric query also surfaces the exact-id match. Other than the popover
 * behavior the textarea behaves like a normal controlled <textarea>.
 */
export const TicketLinkTextarea = forwardRef<HTMLTextAreaElement, Props>(
  function TicketLinkTextarea({ value, onChange, ...rest }, ref) {
    const innerRef = useRef<HTMLTextAreaElement>(null);
    useImperativeHandle(ref, () => innerRef.current!);

    // triggerPos is the index of the "#" character in `value`. Null when popover is closed.
    const [triggerPos, setTriggerPos] = useState<number | null>(null);
    const [query, setQuery] = useState('');
    const [results, setResults] = useState<TicketSummary[]>([]);
    const [highlight, setHighlight] = useState(0);
    const [loading, setLoading] = useState(false);

    // Recompute trigger from value + caret on every change. Closes if "#" is no longer
    // immediately before the caret with no whitespace in between.
    function recomputeTrigger(nextValue: string, caret: number) {
      // Walk backwards from caret looking for the last "#" without whitespace in between.
      let i = caret - 1;
      while (i >= 0) {
        const ch = nextValue[i]!;
        if (ch === '#') {
          // The "#" must be at start-of-string or preceded by whitespace, so we don't
          // trigger on email addresses / hex colors / etc.
          const before = i === 0 ? ' ' : nextValue[i - 1]!;
          if (/\s|[(\[{,;]/.test(before)) {
            setTriggerPos(i);
            setQuery(nextValue.slice(i + 1, caret));
            return;
          }
          break;
        }
        if (/\s/.test(ch)) break;
        i -= 1;
      }
      setTriggerPos(null);
      setQuery('');
      setResults([]);
    }

    function handleChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
      const next = e.target.value;
      onChange(next);
      recomputeTrigger(next, e.target.selectionStart ?? next.length);
    }

    function handleKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
      if (triggerPos == null || results.length === 0) {
        if (triggerPos != null && e.key === 'Escape') {
          e.preventDefault();
          setTriggerPos(null);
        }
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlight((h) => Math.min(results.length - 1, h + 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlight((h) => Math.max(0, h - 1));
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        const pick = results[highlight];
        if (pick) insertLink(pick.id);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setTriggerPos(null);
      }
    }

    function insertLink(ticketId: number) {
      if (triggerPos == null) return;
      const before = value.slice(0, triggerPos);
      const after = value.slice(triggerPos + 1 + query.length);
      const insertion = `#${ticketId}`;
      const next = `${before}${insertion}${after}`;
      onChange(next);
      setTriggerPos(null);
      setQuery('');
      setResults([]);
      setHighlight(0);
      // Restore caret to right after the inserted link.
      setTimeout(() => {
        const ta = innerRef.current;
        if (!ta) return;
        const pos = before.length + insertion.length;
        ta.focus();
        ta.setSelectionRange(pos, pos);
      }, 0);
    }

    // Debounced search whenever the popover is open and the query changes.
    useEffect(() => {
      if (triggerPos == null) return;
      let cancelled = false;
      setLoading(true);
      const t = setTimeout(async () => {
        try {
          // If the query is empty, surface the most recent tickets; otherwise search.
          const params: Record<string, string | number> = { limit: 8, view: 'all' };
          if (query) params.q = query;
          const r = await Tickets.list(params);
          let items = r.tickets;
          // If the user typed a number, also try to fetch that ticket directly and
          // pin it to the top (covers the "#19" case where the title doesn't contain "19").
          const n = parseInt(query, 10);
          if (Number.isFinite(n) && n > 0 && !items.some((it) => it.id === n)) {
            try {
              const direct = await Tickets.get(n);
              items = [direct.ticket, ...items];
            } catch { /* not found, ignore */ }
          }
          if (cancelled) return;
          setResults(items.slice(0, 8));
          setHighlight(0);
        } finally {
          if (!cancelled) setLoading(false);
        }
      }, 200);
      return () => { cancelled = true; clearTimeout(t); };
    }, [triggerPos, query]);

    return (
      <div className="relative">
        <textarea
          ref={innerRef}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onBlur={() => {
            // Defer so a click on a result still registers.
            setTimeout(() => setTriggerPos(null), 150);
          }}
          {...rest}
        />
        {triggerPos != null && (
          <div className="absolute z-30 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-64 overflow-auto">
            <div className="px-3 py-1.5 border-b border-slate-100 text-xs text-slate-500 flex justify-between">
              <span>
                Link a ticket — typing <code className="font-mono">#{query || '…'}</code>
              </span>
              <span className="text-slate-500">↑↓ navigate · Enter to insert</span>
            </div>
            {loading && results.length === 0 ? (
              <div className="px-3 py-3 text-sm text-slate-500">Searching…</div>
            ) : results.length === 0 ? (
              <div className="px-3 py-3 text-sm text-slate-500">No matches</div>
            ) : (
              results.map((t, idx) => (
                <button
                  key={t.id}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => insertLink(t.id)}
                  className={`w-full text-left px-3 py-2 text-sm ${
                    idx === highlight ? 'bg-brand-50' : 'hover:bg-slate-50'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-slate-500 font-mono shrink-0">#{t.id}</span>
                    <span className="text-slate-900 truncate">{t.title}</span>
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {t.status.replace('_', ' ')} · {t.priority}
                  </div>
                </button>
              ))
            )}
          </div>
        )}
      </div>
    );
  },
);
